import { ToastContext } from "context/ToastContext";
import { Label } from "layout/typography/Label";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { useContext, useMemo, useState } from "react";
import { useGetPracticeTypeSuggestionRulesQuery } from "store/queries/practiceTypeSuggestion";
import {
  useCreateProcedureModalityRangeMutation,
  useDeleteProcedureModalityRangeMutation,
  useGetProcedureModalityRangesQuery,
  useUpdateProcedureModalityRangeMutation,
} from "store/queries/procedureModalityRange";
import styled from "styled-components";
import { PracticeTypeRuleModality } from "types/PracticeTypeSuggestion/PracticeTypeSuggestion";
import {
  IProcedureModalityRange,
  IProcedureModalityRangeSaveRequest,
} from "types/ProcedureModalityRange/ProcedureModalityRange";

import { CptGroupDialog } from "./CptGroupDialog";
import { CptGroupTester } from "./CptGroupTester";
import {
  CPT_GROUPS_INTRO,
  PRECEDENCE_SENTENCE,
  codesInWords,
  serviceLineSummary,
  summaryInWords,
} from "./cptGroupWording";
import { MODALITY_CHOICES, MODALITY_LABELS } from "./serviceTypeRuleWording";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
  .p-datatable-tbody > tr.cpt-group-range-off > td {
    opacity: 0.55;
  }
`;

/** The PUT body that re-saves a range unchanged apart from `patch`. */
export const rangeToRequest = (
  range: IProcedureModalityRange,
  patch: Partial<IProcedureModalityRangeSaveRequest> = {}
): IProcedureModalityRangeSaveRequest => ({
  modality: range.modality,
  fromCode: range.fromCode,
  toCode: range.toCode,
  note: range.note,
  enabled: range.enabled,
  ...patch,
});

/** The filter's "every group" choice. A Dropdown cannot hold null as a distinct option value. */
const EVERY_GROUP = "__every__";

/**
 * CLE-3000 — Eligibility › CPT Groups, Ani's "service line map": the CPT / HCPCS code ranges behind
 * each procedure type the Service Type rules key on (MRI, CT, ultrasound, nuclear medicine, sleep
 * study, other diagnostic test, X-ray). Global — every hospital uses the same groups.
 *
 * At the top, the precedence line and a read-only summary per group (its ranges, and the enabled
 * Service Type rules that key on it); editing rules stays on the Service Type Rules tab. Below, one
 * table of ranges with a group filter, an Add / Edit dialog, an Enabled switch that re-saves the
 * row, and "Classify codes", a tester. Mirrors the Service Type Rules tab. Precedence between
 * groups is fixed on the API and is not editable here, so the radiology nesting (70336 TMJ MRI
 * inside the head X-ray range) cannot be configured away.
 */
export function CptGroupsTab() {
  const toast = useContext(ToastContext);
  const [filter, setFilter] = useState<PracticeTypeRuleModality | null>(null);
  const {
    data: ranges,
    isLoading,
    isError,
  } = useGetProcedureModalityRangesQuery();
  // Every rule — all hospitals and each hospital — for the summary.
  const { data: rules } = useGetPracticeTypeSuggestionRulesQuery(null);
  const [createRange, { isLoading: creating }] =
    useCreateProcedureModalityRangeMutation();
  const [updateRange, { isLoading: updating }] =
    useUpdateProcedureModalityRangeMutation();
  const [deleteRange] = useDeleteProcedureModalityRangeMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [testerOpen, setTesterOpen] = useState(false);
  const [editing, setEditing] = useState<IProcedureModalityRange | null>(null);

  const allRanges = ranges ?? [];
  const summary = useMemo(
    () => serviceLineSummary(allRanges, rules ?? []),
    [allRanges, rules]
  );
  const shown = filter
    ? allRanges.filter((range) => range.modality === filter)
    : allRanges;

  const fail = (summaryText: string, error: any, fallback: string) =>
    toast?.current?.show({
      severity: "error",
      summary: summaryText,
      detail: error?.data?.message || error?.message || fallback,
    });

  const handleSave = async (body: IProcedureModalityRangeSaveRequest) => {
    try {
      if (editing) {
        await updateRange({ id: editing.id, body }).unwrap();
      } else {
        await createRange(body).unwrap();
      }
      setDialogOpen(false);
      toast?.current?.show({
        severity: "success",
        summary: "Range saved",
        detail: "The next eligibility check uses it.",
      });
    } catch (error: any) {
      fail("Not saved", error, "The range could not be saved.");
    }
  };

  const toggle = async (range: IProcedureModalityRange, enabled: boolean) => {
    try {
      await updateRange({
        id: range.id,
        body: rangeToRequest(range, { enabled }),
      }).unwrap();
    } catch (error: any) {
      fail("Not changed", error, "The range could not be changed.");
    }
  };

  const remove = async (range: IProcedureModalityRange) => {
    // eslint-disable-next-line no-alert
    const sure = window.confirm(
      `Remove ${codesInWords(range.fromCode, range.toCode)} from ${
        MODALITY_LABELS[range.modality]
      }? The next eligibility check stops counting these codes as ${
        MODALITY_LABELS[range.modality]
      }.`
    );
    if (!sure) return;
    try {
      await deleteRange(range.id).unwrap();
    } catch (error: any) {
      fail("Not removed", error, "The range could not be removed.");
    }
  };

  const openEditor = (range: IProcedureModalityRange | null) => {
    setEditing(range);
    setDialogOpen(true);
  };

  const groupBody = (range: IProcedureModalityRange) => (
    <span
      className="font-semibold"
      data-testid={`cptGroups.range.${range.id}.group`}
    >
      {MODALITY_LABELS[range.modality] ?? range.modality}
    </span>
  );

  const codesBody = (range: IProcedureModalityRange) => (
    <span
      className="font-mono"
      data-testid={`cptGroups.range.${range.id}.codes`}
    >
      {codesInWords(range.fromCode, range.toCode)}
    </span>
  );

  const noteBody = (range: IProcedureModalityRange) => (
    <span className="text-sm">
      {range.note || <span className="text-color-secondary">—</span>}
    </span>
  );

  const enabledBody = (range: IProcedureModalityRange) => (
    <InputSwitch
      checked={range.enabled}
      onChange={(event) => toggle(range, !!event.value)}
      aria-label="Range enabled"
      data-testid={`cptGroups.range.${range.id}.enabled`}
    />
  );

  const actionsBody = (range: IProcedureModalityRange) => (
    <div className="flex gap-1 justify-content-end">
      <Button
        type="button"
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        aria-label={`Edit ${codesInWords(range.fromCode, range.toCode)}`}
        data-testid={`cptGroups.range.${range.id}.edit`}
        onClick={() => openEditor(range)}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        aria-label={`Remove ${codesInWords(range.fromCode, range.toCode)}`}
        data-testid={`cptGroups.range.${range.id}.remove`}
        onClick={() => remove(range)}
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-3" data-testid="cptGroupsTab">
      <Surface>
        <p
          className="mt-0 mb-2 font-semibold"
          data-testid="cptGroups.precedence"
        >
          {PRECEDENCE_SENTENCE}
        </p>
        <p className="mt-0 mb-3 text-color-secondary">{CPT_GROUPS_INTRO}</p>
        <div className="flex flex-column gap-1" data-testid="cptGroups.summary">
          {summary.map((line) => (
            <span
              key={line.modality}
              data-testid={`cptGroups.summary.${line.modality}`}
            >
              {summaryInWords(line)}
            </span>
          ))}
        </div>
        <small className="block mt-2 text-color-secondary">
          Rules are edited on the Service Type Rules tab.
        </small>
      </Surface>

      <Surface>
        <div className="flex flex-wrap gap-2 mb-2 align-items-end">
          <Button
            type="button"
            icon="pi pi-plus"
            label="Add range"
            className="p-button-sm"
            data-testid="cptGroups.add"
            onClick={() => openEditor(null)}
          />
          <Button
            type="button"
            icon="pi pi-play"
            label="Classify codes"
            className="p-button-sm p-button-outlined"
            data-testid="cptGroups.test"
            onClick={() => setTesterOpen(true)}
          />
          <div className="flex flex-column gap-1 ml-auto">
            <Label htmlFor="cptGroups.filter">Show</Label>
            <Dropdown
              inputId="cptGroups.filter"
              options={[
                { label: "Every group", value: EVERY_GROUP },
                ...MODALITY_CHOICES,
              ]}
              value={filter ?? EVERY_GROUP}
              onChange={(event) =>
                setFilter(
                  !event.value || event.value === EVERY_GROUP
                    ? null
                    : event.value
                )
              }
              style={{ minWidth: "14rem" }}
            />
          </div>
        </div>

        {isLoading && (
          <div className="flex justify-content-center p-3">
            <ProgressSpinner
              style={{ width: "2rem", height: "2rem" }}
              data-testid="cptGroups.loading"
            />
          </div>
        )}
        {isError && (
          <Message
            severity="error"
            text="The CPT groups could not be loaded."
            data-testid="cptGroups.error"
          />
        )}
        {!isLoading && !isError && allRanges.length === 0 && (
          <Message
            severity="info"
            text="No ranges are saved yet, so CPT codes are classified with the built-in ranges (the same ones the seed writes). Once the seed runs they appear here and can be edited."
            data-testid="cptGroups.empty"
          />
        )}
        {!isLoading && !isError && allRanges.length > 0 && (
          <StyledDataTable
            value={shown}
            dataKey="id"
            rowClassName={(range: IProcedureModalityRange) => ({
              "cpt-group-range-off": !range.enabled,
            })}
            className="w-full"
            data-testid="cptGroups.table"
            emptyMessage="No ranges in this group."
          >
            <Column
              header="Group"
              body={groupBody}
              style={{ width: "12rem" }}
            />
            <Column
              header="Codes"
              body={codesBody}
              style={{ width: "10rem" }}
            />
            <Column header="Note" body={noteBody} />
            <Column
              header="Enabled"
              body={enabledBody}
              style={{ width: "6rem" }}
            />
            <Column body={actionsBody} style={{ width: "6rem" }} />
          </StyledDataTable>
        )}
      </Surface>

      <CptGroupTester
        visible={testerOpen}
        onHide={() => setTesterOpen(false)}
      />

      <CptGroupDialog
        visible={dialogOpen}
        range={editing}
        ranges={allRanges}
        defaultModality={filter}
        saving={creating || updating}
        onSave={handleSave}
        onHide={() => setDialogOpen(false)}
      />
    </div>
  );
}
